import { useState } from 'react';
import {
  DevHubAvatar,
  DevHubBox,
  DevHubGrid,
  DevHubPaper,
  DevHubTooltip,
  DevHubTypography,
  DevHubListItemText,
  DevHubIconButton,
  DevHubDynamicIcon,
  devhubMakeStyles,
} from '@devxdiscover/devhub-ui';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import {
  ResponsibleParty,
  RolesQueryVariables,
  RoleId,
} from '../types/RoleTypes';
import { DocumentNode } from 'graphql/index';
import { DeleteResponsibility } from './DeleteResponsibility';
import { getPartyName, getPartySecondaryText } from '../utils/partyUtils';

const useStyles = devhubMakeStyles((theme: any) => ({
  partyCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    flex: '1 1 auto',
    minWidth: 'fit-content',
    maxWidth: '100%',
  },
}));

interface RoleByRoleTileProps {
  appId: number;
  roleName: string;
  roleDescription?: string;
  roleId?: number;
  parties: Array<{
    party: ResponsibleParty;
    responsibilityId: number;
  }>;
  rolesQuery: DocumentNode;
  rolesVariables: RolesQueryVariables;
  showDeleteIcon?: boolean;
  showDisabled?: boolean;
  canEditRoles: (roleId: number | undefined) => boolean;
}

/**
 * A component to display a role with its assigned parties
 * @param props the props of the component
 */
function RoleByRoleTile(props: RoleByRoleTileProps) {
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [partySelected, setPartySelected] = useState<{
    party: ResponsibleParty;
    responsibilityId: number;
  }>();
  const [submitted, setSubmitted] = useState(false);

  const getAvatarText = (party: ResponsibleParty) => {
    if (party?.__typename === 'User') {
      const first: string | undefined = party?.firstName?.charAt(0);
      const last: string | undefined = party?.lastName?.charAt(0);
      if (first && last) return first + last;
      return 'U';
    }
    return 'G';
  };

  const handleOpen = (partyData: {
    party: ResponsibleParty;
    responsibilityId: number;
  }) => {
    setPartySelected(partyData);
    setOpen(true);
  };

  const handleClose = () => {
    setSubmitted(false);
    setOpen(false);
  };

  const canEditThisRole = (): boolean => {
    if (props.roleId === RoleId.FunctionalAreaRole) {
      return false;
    }
    return props.canEditRoles(props.roleId);
  };

  const copyEmailToClipboard = (email: string) => {
    navigator.clipboard.writeText(email);
  };

  const getParties = () => {
    return props.parties.map(partyData => {
      const { party, responsibilityId } = partyData;

      return (
        <DevHubBox key={responsibilityId} className={classes.partyCard}>
          <DevHubGrid container padding="small" spacing={1}>
            <DevHubGrid
              item
              xs={12}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <DevHubBox
                display="flex"
                alignItems="center"
                style={{ minWidth: 0, flex: 1 }}
              >
                <DevHubAvatar>
                  <DevHubTypography fontColor="white">
                    {getAvatarText(party)}
                  </DevHubTypography>
                </DevHubAvatar>
                <DevHubBox ml={1.5} style={{ minWidth: 0, flex: 1 }}>
                  <DevHubListItemText
                    primary={
                      <DevHubBox
                        display="flex"
                        alignItems="center"
                        // Note: gap uses inline style because Material-UI v4 doesn't support gap prop
                        style={{ gap: '4px', flexWrap: 'wrap' }}
                      >
                        <span>{getPartyName(party)}</span>
                        {party?.email && (
                          <DevHubTooltip
                            title={
                              <>
                                {party.email}
                                <br />
                                (click to copy)
                              </>
                            }
                          >
                            <DevHubIconButton
                              size="small"
                              onClick={() => copyEmailToClipboard(party.email!)}
                            >
                              <MailOutlineIcon fontSize="small" />
                            </DevHubIconButton>
                          </DevHubTooltip>
                        )}
                      </DevHubBox>
                    }
                    secondary={getPartySecondaryText(party)}
                    secondaryTypographyProps={{
                      style: {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                </DevHubBox>
              </DevHubBox>
              {canEditThisRole() && props.showDeleteIcon && (
                <DevHubIconButton
                  size="small"
                  onClick={() => handleOpen(partyData)}
                >
                  <DevHubDynamicIcon iconName="Delete" color="error" />
                </DevHubIconButton>
              )}
            </DevHubGrid>
          </DevHubGrid>
        </DevHubBox>
      );
    });
  };

  return (
    <DevHubPaper
      slim
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <DevHubGrid container padding="small" alignItems="center">
        <DevHubGrid item xs={12}>
          <DevHubTooltip
            hidden={!props.roleDescription}
            title={props.roleDescription || ''}
          >
            <DevHubTypography variant="h6">{props.roleName}</DevHubTypography>
          </DevHubTooltip>
        </DevHubGrid>
      </DevHubGrid>
      <DevHubBox
        display="flex"
        flexWrap="wrap"
        padding="medium"
        // Note: gap uses inline style because Material-UI v4 doesn't support gap prop
        style={{ gap: '8px' }}
      >
        {getParties()}
      </DevHubBox>
      {partySelected && (
        <DeleteResponsibility
          appId={props.appId}
          party={partySelected.party}
          displayName={props.roleName}
          open={open}
          handleOpen={() => handleOpen(partySelected)}
          handleClose={handleClose}
          responsibilityId={partySelected.responsibilityId}
          rolesQuery={props.rolesQuery}
          rolesVariables={props.rolesVariables}
          setSubmitted={setSubmitted}
          submitted={submitted}
        />
      )}
    </DevHubPaper>
  );
}

export default RoleByRoleTile;
